import toast from 'react-hot-toast';
import type { LikelihoodRanges } from '../constants/likelihoodBalance';
import http from './http';

export interface MyCollegesRecord {
  id?: number;
  student_id?: number;
  colleges_array?: string;
}

const collegeServices = {
  async getState() {
    const res = await http.get(`api/us-college-list/cen-state-types`);
    return res;
  },

  // async getCollege(body: any) {
  //     let requestBody = {}
  //     if (body?.limit_item > 0) {
  //       requestBody = {
  //         ...body,
  //         limit_item: body.keyword.length < 5 ? 10 : body.limit_item,
  //       };
  //     }
  //     else {
  //       requestBody = {
  //           ...body,
  //           limit_item: body.keyword.length < 5 ? 10 : 1000,
  //       };
  //   }

  //   const res = await http.post('api/us-college-list', requestBody);
  //   return res;
  // },

  async getCollegeEx(body: any, controller?: AbortController) {
    let data: any[] = [];
    let requestBody = {};
    if (body?.limit_item > 0) {
      requestBody = {
        ...body,
        limit_item: body.keyword.length < 5 ? 10 : body.limit_item,
      };
    }
    else {
      requestBody = {
          ...body,
          limit_item: body.keyword.length < 5 ? 10 : 1000,
      }
    }
      
    try {
      const res:any = await http.post('api/college-board', requestBody, {
        signal: controller?.signal,
      });
      data = res;
    }
    catch(ex){
      // skip
    }

    return data;
  },

  async searchCollege(keyword: string, state: string, country: string, 
                         searchOptions: string, controller?: AbortController): Promise<any> {
    let data: any[] = [];
    try {
      const body = {
        keyword : keyword,
        options: searchOptions,
        state: state,
        country: country
      }
      data = await http.post('api/college-board', body, {
        signal: controller?.signal,
      });
    }
    catch(ex){
      // skip
    }
    return data;
  },

  
  async findCollege(keyword: string, controller?: AbortController): Promise<any> {
    let data: any[] = [];
    try {
      const body = {
        keyword : keyword,
        options: '',
        state: null,
        country: null,
        limit: 50
      }
      data = await http.post('api/college-board', body, {
        signal: controller?.signal,
      });
    }
    catch(ex){
      // skip
    }
    return data;
  },

  async getDetail(studentId: any) {
    const res = await http.get(`api/college-information/${studentId}`);
    return res;
  },
  async updateDetail(body: any) {
    const res = await http.put(`api/college-information`, body);
    return res;
  },
  async createDetail(body: any) {
    const res = await http.post(`api/college-information`, body);
    return res;
  },
  async getUSCollegeV1(body: any) {
    const res = await http.post(`api/us-colleges`, body);
    return res;
  },
  async getUSCollegeSportV1(body: { keyword: string; college_id?: string }) {
    const res = await http.post(`api/us-colleges/us-sports`, body);
    return res;
  },
  async getUSCollegeMajorV1(body: { keyword: string; college_id?: string }) {
    const res = await http.post(`api/us-colleges/us-majors`, body);
    return res;
  },
  async getUSCollegeMajorCategoriesV1(body: { name?: string } = {}) {
    const res = await http.post(`api/us-colleges/us-major-categories`, body);
    return res;
  },
  /** gm_schools.major_categories — used for majors-of-interest dropdowns. */
  async getMajorInterestCategoryOptions(name = ''): Promise<string[]> {
    const res: any = await this.getUSCollegeMajorCategoriesV1({ name });
    return (res ?? [])
      .map((item: { name: string }) => item?.name)
      .filter(Boolean)
      .sort();
  },
  async getMyCollege(id: string | number): Promise<MyCollegesRecord> {
    return (await http.get(`api/us-colleges/my-colleges/${id}`)) as unknown as MyCollegesRecord;
  },

  // SPEC_College_Search_Filters (v1.1) — per-user filter persistence shared
  // between GradMap College Search and CAM Select Colleges.
  async getSearchFilters(studentId: string | number): Promise<Record<string, any>> {
    try {
      return (await http.get(
        `api/us-colleges/search-filters/${studentId}`,
      )) as unknown as Record<string, any>;
    } catch {
      return {};
    }
  },
  async saveSearchFilters(
    studentId: string | number,
    filters: Record<string, any>,
  ): Promise<void> {
    try {
      await http.put(`api/us-colleges/search-filters/${studentId}`, { filters });
    } catch {
      // best-effort: never block the search UX on a persistence failure
    }
  },

  // SPEC_Undecided_Toggle (v1.1) — student-level "I'm undecided about my Areas
  // of Interest right now" flag. Persisted on student.preferences and shared
  // between GradMap My Colleges and CAM Organize Your Schools.
  async getAreasOfInterestUndecided(
    studentId: string | number,
  ): Promise<boolean> {
    try {
      const value = await http.get(
        `api/students/areas-of-interest-undecided/${studentId}`,
      );
      // GET is `{ undecided: boolean }` (PR #41). Fall back to a raw boolean
      // so a pre-normalization response still parses instead of becoming true
      // via Boolean({ ... }).
      if (typeof value === 'boolean') return value;
      if (value && typeof value === 'object' && 'undecided' in value) {
        return Boolean((value as { undecided: unknown }).undecided);
      }
      return false;
    } catch {
      // best-effort: default to "not undecided" if the fetch fails
      return false;
    }
  },
  async saveAreasOfInterestUndecided(
    studentId: string | number,
    undecided: boolean,
  ): Promise<void> {
    await http.put(`api/students/areas-of-interest-undecided/${studentId}`, {
      undecided: Boolean(undecided),
    });
  },
  async getCamOpenContext(
    studentId: string | number,
    legacyCollegeId?: number | null,
  ): Promise<{
    studentExternalId: string | null;
    processExternalId: string | null;
    schoolExternalId: string | null;
    schoolCamId: string | null;
    requirementsReturnTo: string | null;
    likelihoodRanges: LikelihoodRanges;
  }> {
    const qs =
      legacyCollegeId != null && !Number.isNaN(legacyCollegeId)
        ? `?legacy_college_id=${legacyCollegeId}`
        : '';
    return (await http.get(
      `api/us-colleges/cam-open-context/${studentId}${qs}`,
    )) as unknown as {
      studentExternalId: string | null;
      processExternalId: string | null;
      schoolExternalId: string | null;
      schoolCamId: string | null;
      requirementsReturnTo: string | null;
      likelihoodRanges: import('../constants/likelihoodBalance').LikelihoodRanges;
    };
  },

  /** CAM-CATEGORIES-001: list CAM school_categories + current category/major per school. */
  async getCamCategories(studentId: string | number): Promise<{
    categories: Array<{ id: string; name: string; color: string; isDefault: boolean }>;
    schoolCategoryByLegacyId: Record<string, string | null>;
    schoolMajorByLegacyId: Record<
      string,
      { intendedMajor: string | null; alternateMajor: string | null }
    >;
    schoolCategoryBySchoolId?: Record<string, string | null>;
    schoolMajorBySchoolId?: Record<
      string,
      { intendedMajor: string | null; alternateMajor: string | null }
    >;
  }> {
    return (await http.get(
      `api/us-colleges/my-colleges/${studentId}/cam-categories`,
    )) as unknown as {
      categories: Array<{ id: string; name: string; color: string; isDefault: boolean }>;
      schoolCategoryByLegacyId: Record<string, string | null>;
      schoolMajorByLegacyId: Record<
        string,
        { intendedMajor: string | null; alternateMajor: string | null }
      >;
      schoolCategoryBySchoolId?: Record<string, string | null>;
      schoolMajorBySchoolId?: Record<
        string,
        { intendedMajor: string | null; alternateMajor: string | null }
      >;
    };
  },

  /** CAM-CATEGORIES-001: bulk move schools (by school_id and/or legacy college id) into a CAM category. */
  async moveSchoolsToCamCategory(
    studentId: string | number,
    legacyCollegeIds: number[],
    categoryId: string | null,
    schoolIds: number[] = [],
  ) {
    return http.post(
      `api/us-colleges/my-colleges/${studentId}/cam-categories/move`,
      { legacyCollegeIds, schoolIds, categoryId },
    );
  },

  /** CAM-CATEGORIES-001: create a new CAM school_categories row for this student. */
  async createCamCategory(
    studentId: string | number,
    name: string,
    color: string,
  ): Promise<{ id: string; name: string; color: string; isDefault: boolean }> {
    return (await http.post(
      `api/us-colleges/my-colleges/${studentId}/cam-categories`,
      { name, color },
    )) as unknown as { id: string; name: string; color: string; isDefault: boolean };
  },

  /** CAM-CATEGORIES-001: Primary/Alternate Major options available per school. */
  async getMajorOptions(
    legacyCollegeIds: number[] = [],
    schoolIds: number[] = [],
  ): Promise<Record<string, Array<{ name: string }>>> {
    return (await http.post(`api/us-colleges/my-colleges/cam-major-options`, {
      legacyCollegeIds,
      schoolIds,
    })) as unknown as Record<string, Array<{ name: string }>>;
  },

  /** CAM-CATEGORIES-001: set Primary/Alternate Major for a school before moving categories. */
  async setSchoolMajor(
    studentId: string | number,
    legacyCollegeId: number | null,
    primaryMajor: string,
    alternateMajor: string | null,
    schoolId?: number | null,
  ) {
    return http.post(
      `api/us-colleges/my-colleges/${studentId}/cam-school-major`,
      { legacyCollegeId, schoolId, primaryMajor, alternateMajor },
    );
  },

  async createMyCollege(student_id: string | number, colleges_array: string) {
    const res: any = await http.post(`api/us-colleges/my-colleges`, { student_id, colleges_array });
    toast.success(res.message);
    return res;
  },
  async updatetMyCollege(student_id: string | number, colleges_array: string) {
    const res: any = await http.put(`api/us-colleges/my-colleges`, { student_id, colleges_array });
    toast.success(res.message);
    return res;
  },

  async deleteCollegeData(id: string | number) {
    const res: any = await http.delete(`api/us-colleges/my-colleges/${id}`);
    toast.success(res.message);
    return res;
  },

  async getCollegeLists(studentId: string | number): Promise<{
    studentLists: Array<{
      id: number;
      name: string;
      description?: string | null;
      created_by: 'student' | 'counselor';
      counselor_name?: string | null;
      collegeList: Record<string, unknown>[];
    }>;
    counselorLists: Array<{
      id: number;
      name: string;
      description?: string | null;
      created_by: 'student' | 'counselor';
      counselor_name?: string | null;
      collegeList: Record<string, unknown>[];
    }>;
  }> {
    return (await http.get(`api/college-lists/student/${studentId}`)) as {
      studentLists: Array<{
        id: number;
        name: string;
        description?: string | null;
        created_by: 'student' | 'counselor';
        counselor_name?: string | null;
        collegeList: Record<string, unknown>[];
      }>;
      counselorLists: Array<{
        id: number;
        name: string;
        description?: string | null;
        created_by: 'student' | 'counselor';
        counselor_name?: string | null;
        collegeList: Record<string, unknown>[];
      }>;
    };
  },

  async createCollegeList(
    studentId: string | number,
    name: string,
    description?: string | null,
  ) {
    return http.post('api/college-lists', {
      student_id: Number(studentId),
      name,
      description,
    });
  },

  async createCounselorList(
    studentId: string | number,
    name: string,
    description?: string | null,
  ) {
    return http.post('api/college-lists/counselor', {
      student_id: Number(studentId),
      name,
      description,
    });
  },

  async deleteCounselorList(listId: number) {
    return http.delete(`api/college-lists/counselor/${listId}`);
  },

  async updateCollegeList(
    listId: number,
    patch: { name?: string; description?: string | null },
  ) {
    return http.patch(`api/college-lists/${listId}`, patch);
  },

  async deleteCollegeList(listId: number) {
    const res: any = await http.delete(`api/college-lists/${listId}`);
    return res;
  },

  async addCollegeListItem(
    listId: number,
    studentId: string | number,
    college: Record<string, unknown>,
  ) {
    return http.post(`api/college-lists/${listId}/items`, {
      student_id: Number(studentId),
      college,
    });
  },

  async removeCollegeListItem(
    listId: number,
    itemId: number,
    studentId: string | number,
  ) {
    return http.delete(
      `api/college-lists/${listId}/items/${itemId}?student_id=${studentId}`,
    );
  },

  async removeCollegeFromList(
    listId: number,
    legacyCollegeId: number,
    studentId: string | number,
  ) {
    return http.delete(
      `api/college-lists/${listId}/colleges/${legacyCollegeId}?student_id=${studentId}`,
    );
  },

  async moveCollegeToMySchools(
    studentId: string | number,
    college: Record<string, unknown>,
  ) {
    return http.post(`api/college-lists/student/${studentId}/move-to-my-schools`, {
      college,
    });
  },
  async sendMailResetPassword(body: { email: string; username: string }) {
    // const res: any = await http.post(`api/notification/email/chat`, body);
    // toast.success(res.message);
    // return res;
  },

  async sendMailChat(body: {
    email: string;
    sender: string;
    receiver: string;
    content: string;
    linkPage: string;
  }) {
    const res: any = await http.post(`api/notification/email/chat`, body);
    toast.success(res.message);
    return res;
  },
  async sendMailUserMessage(body: {
    email: string;
    sender: string;
    receiver: string;
    content: string;
    linkPage: string;
  }) {
    const res: any = await http.post(`api/notification/email/user-message`, body);
    toast.success(res.message);
    return res;
  },

  async sendMailWelcome(body: { email: string; username: string, isSend: boolean }) {
    if(!body.isSend) return;
    const res: any = await http.post(`api/notification/email/registration`, body);
    toast.success(res.message);
    return res;
  },

  async sendMailWelcomeCounselor(body: { email: string; username: string }) {
    const res: any = await http.post(`api/notification/email/counselor-registration`, body);
    toast.success(res.message);
    return res;
  },

  async sendMailWelcomeParent(body: { email: string; username: string }) {
    const res: any = await http.post(`api/notification/email/parent-registration`, body);
    toast.success(res.message);
    return res;
  },

  async sendMailInvite(body: {
    email: string;
    username: string;
    studentFirstname: string;
    studentLastname: string;
  }) {
    const res: any = await http.post(`api/notification/email/counselor-invitation`, body);
    toast.success(res.message);
    return res;
  },
  
  async sendStudentInvitationParentEmail(body: {
    parentEmail: string;
    sender: string;
    username: string;
    studentFirstname: string;
    studentLastname: string;
    approveUrl: string;
    /** Tên phụ huynh được mời (đặc biệt khi mời ngoài hệ thống). */
    parentFirstName?: string;
    parentLastName?: string;
  }) {
    const res: any = await http.post(`api/notification/email/student/parent-invitation`, body);
    toast.success(res.message);
    return res;
  },

  async sendParentInvitationStudentEmail(body: {
    studentEmail: string; 
    sender: string;  
    username: string;
    parentFirstname: string; 
    parentLastname: string;
    parentEmail: string;
    approveUrl: string; 
  }) {
    const res: any = await http.post(`api/notification/email/parent/parent-invitation`, body);
    toast.success(res.message);
    return res;
  },


  async sendMailNewParentCollege(body: any) {
    const res: any = await http.post(`api/notification/email/new-parent-college`, body);
    return res;
  }
};

export default collegeServices;
